import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Async } from '../components/Async';
import { PCard } from '../components/PCard';
import { Page, PageHeader } from '../components/Page';
import { StatusPill } from '../components/StatusPill';
import {
  getFedexShipment,
  type FedexChargeRow,
  type FedexShipmentSla,
} from '../lib/api';
import { fmtCents, fmtDateTimeInZone, fmtUtcDate } from '../lib/format';
import { useFetch } from '../lib/useFetch';

const PHARMACY_ADMIN = 'https://pharmacy.5axis.health';

const SLA_REASON_LABELS: Record<NonNullable<FedexShipmentSla['reason']>, string> = {
  missing_in_shipping: 'FedEx received date is missing.',
  missing_delivery: 'Delivery date is missing.',
  unsupported_service: 'This service does not have a supported SLA rule.',
  invalid_timestamps: 'Shipment timestamps are invalid.',
};

export const FedexShipmentDetailPage = () => {
  const { invoiceId = '', chargeId = '' } = useParams();
  const detail = useFetch(
    () => getFedexShipment(invoiceId, chargeId),
    [invoiceId, chargeId],
  );

  return (
    <Page>
      <Async state={detail}>
        {({ invoice, charge }) => {
          const tracking = charge.trackingNumber ?? `…${charge._id.slice(-8)}`;
          const packageType = charge.packageType ?? 'Not captured';
          const otherFedexCharges =
            charge.transportationCents === undefined
              ? undefined
              : charge.realCostCents -
                charge.transportationCents -
                (charge.discountCents ?? 0);

          return (
            <>
              <PageHeader
                title={`Shipment ${tracking}`}
                sub={
                  <>
                    <Link className="fedex-back-link" to={`/fedex/${invoice.id}`}>
                      FedEx {invoice.fedexInvoiceNumber ?? invoice.fileName}
                    </Link>
                    {invoice.invoiceDate ? ` · ${fmtUtcDate(invoice.invoiceDate)}` : ''}
                  </>
                }
                actions={
                  charge.pharmacyOrderId ? (
                    <a
                      className="btn btn--ghost"
                      href={`${PHARMACY_ADMIN}/orders/${encodeURIComponent(charge.pharmacyOrderId)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open pharmacy order ↗
                    </a>
                  ) : undefined
                }
              />

              <section className="fedex-shipment-hero" aria-label="FedEx billed package summary">
                <div>
                  <div className="fedex-shipment-hero__eyebrow">FedEx billed package</div>
                  <div className="fedex-shipment-hero__package">{packageType}</div>
                  <div className="fedex-shipment-hero__meta">
                    {charge.serviceTypeText ?? 'Service not captured'}
                    {charge.zone ? ` · Zone ${charge.zone}` : ''}
                  </div>
                </div>
                <div className="fedex-shipment-hero__total">
                  <span>FedEx total</span>
                  <strong>{fmtCents(charge.realCostCents)}</strong>
                </div>
              </section>

              <ShipmentStatus charge={charge} />

              <DeliverySlaCard charge={charge} />

              <div className="grid grid-2 fedex-detail-grid">
                <PCard style={{ padding: 20 }}>
                  <h3 className="fedex-detail-heading">FedEx shipment facts</h3>
                  <dl className="fedex-detail-list">
                    <Detail label="Tracking" mono>{charge.trackingNumber ?? 'Not captured'}</Detail>
                    <Detail label="Ship date">{charge.shipDate ? fmtUtcDate(charge.shipDate) : 'Not captured'}</Detail>
                    <Detail label="Service">{charge.serviceTypeText ?? 'Not captured'}</Detail>
                    <Detail label="Package type">{charge.packageType ?? 'Not captured'}</Detail>
                    <Detail label="Automation">{charge.automation ?? 'Not captured'}</Detail>
                    <Detail label="Zone">{charge.zone ?? 'Not captured'}</Detail>
                    <Detail label="FedEx customer ref." mono>{charge.custRef ?? 'Not captured'}</Detail>
                  </dl>
                </PCard>

                <PCard style={{ padding: 20 }}>
                  <h3 className="fedex-detail-heading">FedEx charge breakdown</h3>
                  <dl className="fedex-detail-list">
                    <Detail label="Transportation" money>
                      {money(charge.transportationCents)}
                    </Detail>
                    <Detail label="Discount" money>
                      {money(charge.discountCents)}
                    </Detail>
                    <Detail label="Other FedEx charges" money>
                      {money(otherFedexCharges)}
                    </Detail>
                    <Detail label="Total billed by FedEx" money strong>
                      {fmtCents(charge.realCostCents)}
                    </Detail>
                  </dl>
                  <p className="fedex-detail-note">
                    Other FedEx charges are derived from the shipment total after transportation
                    and any listed discount. Use the uploaded invoice as the source document for a dispute.
                  </p>
                </PCard>

                <PCard style={{ padding: 20, gridColumn: '1 / -1' }}>
                  <h3 className="fedex-detail-heading">Pharmacy match and billing comparison</h3>
                  <dl className="fedex-detail-list fedex-detail-list--wide">
                    <Detail label="Brand">{charge.clinic ?? 'Not matched'}</Detail>
                    <Detail label="Pharmacy order">
                      {charge.pharmacyOrderId ? (
                        <a
                          className="fedex-inline-link"
                          href={`${PHARMACY_ADMIN}/orders/${encodeURIComponent(charge.pharmacyOrderId)}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {charge.pharmacyOrderId} ↗
                        </a>
                      ) : (
                        'Not available'
                      )}
                    </Detail>
                    <Detail label="Fill ID" mono>{charge.fillId ?? 'Not available'}</Detail>
                    <Detail label="Match method">
                      {charge.matched
                        ? `${charge.matchSource ?? 'source'} · ${charge.matchStrategy?.replace('_', ' ') ?? 'matched'}`
                        : 'Unmatched'}
                    </Detail>
                    <Detail label="Rate class">{charge.shippedService?.replace('_', ' ') ?? 'Not mapped'}</Detail>
                    <Detail label="Already billed">{charge.billed ? 'Yes' : 'No'}</Detail>
                    <Detail label="We charged" money>{money(charge.chargedCents)}</Detail>
                    <Detail label="Margin" money>{money(charge.marginCents)}</Detail>
                  </dl>
                </PCard>
              </div>
            </>
          );
        }}
      </Async>
    </Page>
  );
};

function ShipmentStatus({ charge }: { charge: FedexChargeRow }) {
  return (
    <div className="fedex-shipment-status" aria-label="Shipment audit status">
      <StatusPill tone={charge.matched ? 'success' : 'error'}>
        {charge.matched ? 'matched' : 'unmatched'}
      </StatusPill>
      {charge.billed && <StatusPill tone="success">billed</StatusPill>}
      <SlaStatusPill sla={charge.sla} />
      {charge.flags.filter((flag) => flag !== 'unmatched').map((flag) => (
        <StatusPill
          key={flag}
          tone={flag === 'negative_margin' || flag === 'unmatched' ? 'error' : 'attention'}
        >
          {flag.replace(/_/g, ' ')}
        </StatusPill>
      ))}
    </div>
  );
}

function DeliverySlaCard({ charge }: { charge: FedexChargeRow }) {
  const { sla } = charge;
  const timeZoneLabel = sla.timeZone === 'America/New_York' ? 'ET' : sla.timeZone;
  const reason = sla.reason ? SLA_REASON_LABELS[sla.reason] : undefined;
  const statusSummary =
    sla.status === 'late'
      ? sla.businessDaysLate === null
        ? 'Delivery missed the calculated promise.'
        : sla.businessDaysLate === 0
          ? 'Delivery was late by less than one business day.'
          : `Delivery was ${sla.businessDaysLate} business ${sla.businessDaysLate === 1 ? 'day' : 'days'} late.`
      : sla.status === 'on_time'
        ? 'Delivery met the calculated promise.'
        : (reason ?? 'The available evidence is not enough to evaluate this shipment.');

  return (
    <PCard style={{ padding: 20 }}>
      <section aria-labelledby="fedex-delivery-sla-heading">
        <div className="fedex-sla-card__header">
          <div>
            <div className="fedex-sla-card__eyebrow">Carrier service audit</div>
            <h3 id="fedex-delivery-sla-heading" className="fedex-detail-heading">
              Delivery SLA evidence
            </h3>
            <p className="fedex-sla-card__summary">{statusSummary}</p>
          </div>
          <SlaStatusPill sla={sla} />
        </div>

        <ol className="fedex-sla-timeline" aria-label="Delivery SLA timeline">
          <SlaMilestone
            label="FedEx received"
            date={sla.inShippingDate}
            timestamp={charge.inShippingAt}
            timeZone={sla.timeZone}
            timeZoneLabel={timeZoneLabel}
          />
          <SlaMilestone
            label="Promised by"
            date={sla.expectedDeliveryDate}
            timeZone={sla.timeZone}
            timeZoneLabel={timeZoneLabel}
          />
          <SlaMilestone
            label="Delivered"
            date={sla.deliveredDate}
            timestamp={charge.deliveredAt}
            timeZone={sla.timeZone}
            timeZoneLabel={timeZoneLabel}
          />
        </ol>

        <p className="fedex-detail-note fedex-sla-card__note">
          Delivery timestamp source: {deliverySourceLabel(charge.deliveryTimestampSource)}. Dates are
          normalized to {sla.timeZone}{timeZoneLabel !== sla.timeZone ? ` (${timeZoneLabel})` : ''}.
          SLA rule: weekends-only v1; holidays and carrier exceptions are not applied.
        </p>
      </section>
    </PCard>
  );
}

function SlaMilestone({
  label,
  date,
  timestamp,
  timeZone,
  timeZoneLabel,
}: {
  label: string;
  date: string | null;
  timestamp?: string;
  timeZone: string;
  timeZoneLabel: string;
}) {
  return (
    <li className={date ? 'fedex-sla-timeline__item' : 'fedex-sla-timeline__item fedex-sla-timeline__item--missing'}>
      <span className="fedex-sla-timeline__marker" aria-hidden="true" />
      <span className="fedex-sla-timeline__label">{label}</span>
      {date ? (
        <time dateTime={timestamp ?? date}>
          {timestamp ? fmtDateTimeInZone(timestamp, timeZone) : fmtUtcDate(date)}{' '}
          <small>{timeZoneLabel}</small>
        </time>
      ) : (
        <span className="fedex-sla-timeline__missing">Not available</span>
      )}
    </li>
  );
}

function SlaStatusPill({ sla }: { sla: FedexShipmentSla }) {
  if (sla.status === 'late') {
    const days = sla.businessDaysLate;
    return (
      <StatusPill tone="error">
        {days === null
          ? 'SLA late'
          : days === 0
            ? 'SLA late · under 1 business day'
            : `${days} business ${days === 1 ? 'day' : 'days'} late`}
      </StatusPill>
    );
  }
  if (sla.status === 'on_time') return <StatusPill tone="success">SLA on time</StatusPill>;
  return <StatusPill tone="neutral">SLA unknown</StatusPill>;
}

function deliverySourceLabel(source?: FedexChargeRow['deliveryTimestampSource']): string {
  if (source === 'status_history') return 'status history';
  if (source === 'fill_timestamp') return 'fill timestamp';
  return 'not available';
}

function Detail({
  label,
  children,
  mono = false,
  money: isMoney = false,
  strong = false,
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
  money?: boolean;
  strong?: boolean;
}) {
  return (
    <div className="fedex-detail-item">
      <dt>{label}</dt>
      <dd
        className={`${mono ? 'fedex-detail-item--mono ' : ''}${isMoney ? 'tabular ' : ''}${strong ? 'fedex-detail-item--strong' : ''}`.trim()}
      >
        {children}
      </dd>
    </div>
  );
}

function money(cents?: number): string {
  return cents === undefined ? 'Not captured' : fmtCents(cents);
}
